'use client';

import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// Different sizes for the loading spinner
export type LoaderSize = 'xs' | 'sm' | 'md' | 'lg';

interface LoadingProps {
  size?: LoaderSize;
  text?: string;
  fullHeight?: boolean;
  className?: string;
  textClassName?: string;
  iconClassName?: string;
}

// Size mappings for consistent loading indicators
const sizeMap = {
  xs: 'h-4 w-4',
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
};

const borderMap = {
  xs: 'border-2',
  sm: 'border-2',
  md: 'border-4',
  lg: 'border-4',
};

/**
 * A standardized loading component for consistent loading UX across the application
 */
export function Loading({
  size = 'lg',
  text,
  fullHeight = false,
  className,
  textClassName,
  iconClassName,
}: LoadingProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center',
        fullHeight && 'h-64',
        className
      )}
    >
      <div
        className={cn(
          sizeMap[size],
          borderMap[size],
          'animate-spin rounded-full border-[#8A6FE8] border-t-transparent',
          iconClassName
        )}
      />
      {text && (
        <p className={cn('mt-2 text-gray-600', size === 'lg' ? 'text-lg' : 'text-sm', textClassName)}>
          {text}
        </p>
      )}
    </div>
  );
}

/**
 * Button loading spinner - for use inside buttons
 */
export function ButtonLoading({ size = 'sm', className }: { size?: 'sm' | 'xs'; className?: string }) {
  return (
    <div
      className={cn(
        sizeMap[size],
        borderMap[size],
        'animate-spin rounded-full border-white border-t-transparent mr-2',
        className
      )}
    />
  );
}

export default Loading; 